import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { SLOT_BY_KEY, SLOT_KEY_BY_FAMILY, CANONICAL_CONTENT_FIELD_BY_FAMILY } from "../compositionSlots";
import { useResolvedAssetVersionContent } from "./useResolvedAssetVersionContent";

/**
 * EmailBuilderCanvasBlock — EMAILS-IMPL-015, rendering fixed by EMAILS-IMPL-026
 *
 * One ordered Canvas occurrence. Sortable (reorder within the Canvas,
 * array position only -- no numeric order field, CRE-SPEC-001 §15.5) AND
 * a drop target in its own right (a library item dropped ON a block
 * inserts BEFORE it -- see builderDnd.js). Selection is keyed by
 * `blockId` only, matching the Landing Page Builder's own established
 * "select by permanent id, never type/index" rule.
 *
 * EMAILS-IMPL-026: the actual pinned Asset VERSION's own canonical
 * content is now the PRIMARY, prominent element -- governed
 * identity/Version is secondary metadata below it (never the reverse).
 * Content resolution is shared with EmailBuilderMetadata.jsx via
 * `useResolvedAssetVersionContent` -- the same existing per-asset
 * Version-history API every Emails panel already uses, never a second
 * data layer or a new backend endpoint. `mode` ("builder" | "preview")
 * controls ONLY visual prominence of the governed-identity line;
 * `readOnly` continues to control drag/remove interactivity exactly as
 * before (both a real Preview and a read-only historical Builder view
 * are `readOnly`, but only Preview visually subordinates the identity
 * line -- a historical Builder view still needs it prominent for the
 * operator's own governance context).
 */
export default function EmailBuilderCanvasBlock({ block, selected, removable, readOnly, mode, onSelect, onRemove }) {
  const slot = SLOT_BY_KEY[SLOT_KEY_BY_FAMILY[block.family]];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: block.blockId,
    disabled: readOnly,
    data: { type: "canvas-block" },
  });

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const resolved = useResolvedAssetVersionContent(slot.versionsFn, block.assetId, block.version);
  const isPreview = mode === "preview";

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`builder-canvas-block-${block.blockId}`}
      data-selected={selected ? "true" : "false"}
      onClick={() => onSelect(block.blockId)}
      className={
        "flex cursor-pointer items-start gap-2 rounded-lg border p-3 transition-colors " +
        (selected ? "border-violet-400/50 bg-violet-500/10" : "border-white/10 bg-white/5 hover:bg-white/[0.07]") +
        (isOver ? " ring-2 ring-violet-400/60" : "")
      }
    >
      {!readOnly && (
        <button
          type="button"
          className="mt-0.5 shrink-0 cursor-grab rounded p-0.5 text-slate-500 hover:text-slate-300"
          aria-label={`Drag to reorder ${slot.label}`}
          {...attributes}
          {...listeners}
          data-testid={`builder-canvas-drag-handle-${block.blockId}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        {!isPreview && (
          <div className="mb-1 flex items-center gap-2 text-xs">
            <span className="rounded bg-white/10 px-1.5 py-0.5 font-medium uppercase tracking-wide text-slate-300">{slot.label}</span>
            {block.role === "echo" && (
              <span className="rounded bg-fuchsia-500/15 px-1.5 py-0.5 font-medium uppercase tracking-wide text-fuchsia-300" data-testid={`builder-canvas-role-${block.blockId}`}>
                Echo
              </span>
            )}
            <span className="font-mono text-slate-500" data-testid={`builder-canvas-blockmeta-${block.blockId}`}>
              {block.assetId} @ {block.version}
            </span>
          </div>
        )}
        <div className="whitespace-pre-wrap text-sm text-slate-100" data-testid={`builder-canvas-blockcontent-${block.blockId}`}>
          <BlockContentPreview family={block.family} resolved={resolved} assetId={block.assetId} version={block.version} />
        </div>
      </div>
      {!readOnly && removable && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 w-6 shrink-0 p-0 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(block.blockId);
          }}
          aria-label={`Remove ${slot.label} block`}
          data-testid={`builder-canvas-remove-${block.blockId}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function BlockContentPreview({ family, resolved, assetId, version }) {
  if (resolved.status === "idle" || resolved.status === "loading") {
    return <span className="text-slate-500">Loading content…</span>;
  }
  // EMAILS-IMPL-026: an explicit diagnostic, never a silent "—" -- covers
  // BOTH the fetch failing AND the fetch succeeding but finding no Version
  // entry matching the exact pinned version (never a current_version
  // fallback either way).
  if (resolved.status === "unresolved") {
    return (
      <span className="text-amber-300" data-testid="builder-canvas-blockunresolved">
        Unable to resolve {assetId} @ {version}
      </span>
    );
  }

  const content = resolved.content;

  if (family === "IMG") {
    const dims = content.width && content.height ? `${content.width}×${content.height}` : null;
    // No safe Image-serving URL exists yet -- governed identity/metadata
    // only (checkpoint §21). Never a fabricated browser-accessible URL.
    const summary = [content.alt_text || "Image", dims, content.content_type].filter(Boolean).join(" · ");
    return summary ? (
      <span className="text-slate-300">{summary}</span>
    ) : (
      <span className="text-amber-300" data-testid="builder-canvas-blockunresolved">
        Unable to resolve {assetId} @ {version}
      </span>
    );
  }

  const field = CANONICAL_CONTENT_FIELD_BY_FAMILY[family];
  const text = field && content[field];
  return text ? (
    <span>{text}</span>
  ) : (
    <span className="text-amber-300" data-testid="builder-canvas-blockunresolved">
      Unable to resolve {assetId} @ {version}
    </span>
  );
}
